import { useEffect, useState } from "react";
import { api, AUD, PCT } from "../lib/api";
import { ErrorCard, parseApiError } from "../components/ErrorCard";

export default function InvestmentCommittee() {
    const [d, setD] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        api.get("/investment-committee")
           .then(r => setD(r.data))
           .catch(e => setError(parseApiError(e)));
    }, []);
    if (error) return <ErrorCard error={error} subject="investment committee" testId="ic-error" />;
    if (!d) return <div className="text-[var(--muted)] p-8" data-testid="ic-loading">Loading…</div>;

    const hasLive = d.live_holdings_priced > 0;
    const delta = hasLive ? d.live_total_value - d.total_financial_assets : 0;

    return (
        <div data-testid="ic-page">
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Investment Committee</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Allocation vs targets.</h1>
                <p className="text-[var(--muted)] mt-3">
                    Total financial assets:{" "}
                    <span className="text-[var(--ink)] font-medium font-mono-num">
                        {AUD(d.total_financial_assets)}
                    </span>
                </p>
            </div>

            {hasLive && (
                <div className="hairline bg-white p-6 rounded mb-8 flex items-baseline justify-between flex-wrap gap-3"
                     data-testid="ic-live-overlay">
                    <div>
                        <div className="label-eyebrow">Live overlay</div>
                        <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
                            {d.live_holdings_priced} of {d.live_holdings_total} holdings priced
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow">Live-priced total</div>
                        <div className="font-serif text-2xl mt-1 font-mono-num"
                             data-testid="ic-live-total">{AUD(d.live_total_value)}</div>
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow">vs Baseline</div>
                        <div className="font-serif text-2xl mt-1 font-mono-num"
                             style={{ color: delta >= 0 ? "var(--eucalyptus)" : "var(--terracotta)" }}>
                            {delta >= 0 ? "+" : ""}{AUD(delta)}
                        </div>
                    </div>
                </div>
            )}

            <div className="hairline bg-white rounded overflow-hidden">
                <table className="editorial w-full" data-testid="ic-table">
                    <thead>
                        <tr>
                            <th>Bucket</th>
                            <th className="num">Value</th>
                            <th className="num">Actual %</th>
                            <th className="num">Target %</th>
                            <th className="num">Variance</th>
                            <th>Allocation</th>
                            <th>Comment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {d.buckets.map(b => {
                            const variance = b.variance;
                            const varColor = Math.abs(variance) > 0.03
                                ? "var(--terracotta)"
                                : Math.abs(variance) > 0.01 ? "var(--gold)" : "var(--eucalyptus)";
                            return (
                                <tr key={b.id}>
                                    <td className="font-medium">{b.bucket}</td>
                                    <td className="num font-mono-num">{AUD(b.value)}</td>
                                    <td className="num font-mono-num">{PCT(b.pct)}</td>
                                    <td className="num font-mono-num text-[var(--muted)]">{PCT(b.target)}</td>
                                    <td className="num font-mono-num" style={{ color: varColor }}>
                                        {variance >= 0 ? "+" : ""}{PCT(variance)}
                                    </td>
                                    <td>
                                        <div className="w-40 h-1.5 bg-[var(--line)] rounded overflow-hidden">
                                            <div className="h-full bg-[var(--eucalyptus)]"
                                                 style={{ width: `${(b.pct || 0) * 100}%` }} />
                                        </div>
                                    </td>
                                    <td className="text-[var(--muted)] text-sm">{b.comment}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
